import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import usePlacesAutocompleteService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { ICity } from 'app/modules/Trip/Overview/Transport/types';
import { searchPlaces, InternalPlace } from 'app/modules/Trip/Overview/Mobility/services';
import { searchStations, InternalStation } from 'app/modules/Trip/Overview/Transport/services';
import { TransportStationPlaceType } from 'app/modules/Trip/Overview/Transport/types';
import { convertAddressOutputToPlaceResult } from 'utils/googlePlacesApi';
import debounce from 'lodash/debounce';
import {
  GOOGLE_PLACES_API_DELAY_AFTER_EMPTY_INTERNAL,
  MIN_PLACE_SEARCH_CHARACTERS,
  PLACE_SEARCH_DEBOUNCE_DELAY,
} from 'configs/constants';

export type PlaceType = 'address' | 'city' | 'establishment';

interface GooglePlacesAutocompleteProps {
  onPlaceSelected: (place: PlaceResult) => void;
  placeholder?: string;
  className?: string;
  dataTestId?: string;
  placeType?: PlaceType;
  countryRestriction?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  suffix?: React.ReactNode;
  // Form validation props
  form?: FormInstance;
  fieldName?: string;
  validationMessage?: string;
  cityContext?: ICity;
  // Internal search props
  enableInternalSearch?: boolean;
  // Station search props (for Transport module)
  enableStationSearch?: boolean;
  stationPlaceType?: TransportStationPlaceType;
  // Configurable delay for Google Places API after empty internal response (in milliseconds)
  googlePlacesDelay?: number;
  // Minimum characters required before calling API (default: 3)
  minSearchCharacters?: number;
  // Debounce delay for API calls (in milliseconds) - waits for user to finish typing
  debounceDelay?: number;
}

export interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface Geometry {
  location: {
    lat: () => number;
    lng: () => number;
  };
}

export interface PlaceResult {
  address_components: AddressComponent[];
  formatted_address: string;
  geometry: Geometry;
  place_id: string;
  html_attributions: string[];
  name: string;
}

const GooglePlacesInput: React.FC<GooglePlacesAutocompleteProps> = ({
  onPlaceSelected,
  placeholder,
  className = '',
  dataTestId,
  countryRestriction,
  value,
  onChange,
  onBlur,
  disabled,
  suffix,
  form,
  fieldName,
  validationMessage,
  cityContext,
  enableInternalSearch = false,
  enableStationSearch = false,
  stationPlaceType,
  googlePlacesDelay: customGooglePlacesDelay,
  minSearchCharacters: customMinSearchCharacters,
  debounceDelay: customDebounceDelay,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [lastSelectedValue, setLastSelectedValue] = useState<string>('');
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const [internalPlaces, setInternalPlaces] = useState<InternalPlace[]>([]);
  const [internalStations, setInternalStations] = useState<InternalStation[]>([]);
  const [isInternalSearchLoading, setIsInternalSearchLoading] = useState(false);
  const [useGooglePlaces, setUseGooglePlaces] = useState(false);
  const [internalSearchCompleted, setInternalSearchCompleted] = useState(false);
  const [googlePlacesSearchCompleted, setGooglePlacesSearchCompleted] = useState(false);
  const googlePlacesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Store stable refs to avoid recreating debounced functions
  const getPlacePredictionsRef = useRef<((request: { input: string }) => void) | null>(null);
  const performInternalSearchRef = useRef<((keyword: string) => Promise<void>) | null>(null);
  const performStationSearchRef = useRef<((keyword: string) => Promise<void>) | null>(null);

  // Use configurable delay or default from constants
  const googlePlacesDelay = customGooglePlacesDelay ?? GOOGLE_PLACES_API_DELAY_AFTER_EMPTY_INTERNAL;
  const minSearchChars = customMinSearchCharacters ?? MIN_PLACE_SEARCH_CHARACTERS;
  const debounceDelayMs = customDebounceDelay ?? PLACE_SEARCH_DEBOUNCE_DELAY;

  // Use translation for validation message if not provided
  const validationMessageText = validationMessage || t('googlePlaces.validationMessage');

  const { placePredictions, getPlacePredictions, isPlacePredictionsLoading, placesService } =
    usePlacesAutocompleteService({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      // Don't use debounce here when internal/station search is enabled - we handle it manually
      // For direct Google Places (signup/profile), use debounce
      debounce: (enableInternalSearch || enableStationSearch) ? 0 : debounceDelayMs,
      options: {
        ...(countryRestriction && { componentRestrictions: { country: countryRestriction } }),
      },
    });

  // Update refs when functions change
  useEffect(() => {
    getPlacePredictionsRef.current = getPlacePredictions;
  }, [getPlacePredictions]);

  // Track when Google Places search completes
  useEffect(() => {
    // For trip services: track Google Places completion after internal search completes
    if (enableInternalSearch || enableStationSearch) {
      if (!isPlacePredictionsLoading && useGooglePlaces && internalSearchCompleted) {
        setGooglePlacesSearchCompleted(true);
        // Stop internal loading when Google Places completes
        setIsInternalSearchLoading(false);
      }
    }
  }, [isPlacePredictionsLoading, useGooglePlaces, internalSearchCompleted, enableInternalSearch, enableStationSearch]);

  // Separate effect for direct Google Places (signup/profile pages only)
  useEffect(() => {
    // Only for direct Google Places (when both internal and station search are disabled)
    // Only update states when loading completes and we have an active search (inputValue has content)
    if (!enableInternalSearch && !enableStationSearch && !isPlacePredictionsLoading && inputValue && inputValue.length >= minSearchChars) {
      setGooglePlacesSearchCompleted(true);
      setInternalSearchCompleted(true);
      setIsInternalSearchLoading(false);
    }
  }, [isPlacePredictionsLoading, enableInternalSearch, enableStationSearch, inputValue, minSearchChars]);

  // Helper: Clear Google Places timeout
  const clearGooglePlacesTimeout = useCallback(() => {
    if (googlePlacesTimeoutRef.current) {
      clearTimeout(googlePlacesTimeoutRef.current);
      googlePlacesTimeoutRef.current = null;
    }
  }, []);

  // Helper: Build search query with city context
  const buildSearchQueryWithCity = useCallback(
    (keyword: string): string => {
      if (!cityContext) return keyword;
      const locationParts = [cityContext.name, cityContext.state?.name, cityContext.country?.name].filter(Boolean);
      return locationParts.length > 0 ? `${keyword} in ${locationParts.join(', ')}` : keyword;
    },
    [cityContext],
  );

  // Helper: Get Google Place types for station search
  const getGooglePlaceTypesForStation = useCallback((placeType: TransportStationPlaceType | undefined): string[] => {
    if (placeType === TransportStationPlaceType.TRAIN_STATION) {
      return ['train_station', 'transit_station'];
    }
    if (placeType === TransportStationPlaceType.BUS_STOP) {
      return ['bus_station', 'transit_station'];
    }
    // For FERRY_PORT, don't use type restrictions - let Google Places return any matching places
    return [];
  }, []);

  // Helper: Set state for empty results and prepare Google Places fallback
  const prepareGooglePlacesFallback = useCallback(() => {
    setInternalPlaces([]);
    setInternalStations([]);
    setUseGooglePlaces(true);
    setInternalSearchCompleted(false);
    setGooglePlacesSearchCompleted(false);
  }, []);

  // Helper: Call Google Places API with proper configuration
  const callGooglePlacesAPI = useCallback((searchQuery: string, placeTypes?: string[]) => {
    if (getPlacePredictionsRef.current) {
      setInternalSearchCompleted(true);
      setGooglePlacesSearchCompleted(false);
      getPlacePredictionsRef.current({
        input: searchQuery,
        ...(placeTypes && placeTypes.length > 0 && { types: placeTypes }),
      });
      googlePlacesTimeoutRef.current = null;
    }
  }, []);

  // Helper: Schedule Google Places API call after delay
  const scheduleGooglePlacesCall = useCallback(
    (trimmedKeyword: string, placeTypes?: string[]) => {
      googlePlacesTimeoutRef.current = setTimeout(() => {
        if (googlePlacesTimeoutRef.current !== null) {
          const searchQuery = buildSearchQueryWithCity(trimmedKeyword);
          callGooglePlacesAPI(searchQuery, placeTypes);
        }
      }, googlePlacesDelay);
    },
    [googlePlacesDelay, buildSearchQueryWithCity, callGooglePlacesAPI],
  );

  // Station search function - called after debounce and minimum character check
  const performStationSearch = useCallback(
    async (keyword: string) => {
      clearGooglePlacesTimeout();

      if (!enableStationSearch || !stationPlaceType || !keyword || keyword.trim().length === 0) {
        setInternalStations([]);
        setUseGooglePlaces(false);
        return;
      }

      // Only call API if minimum characters requirement is met
      const trimmedKeyword = keyword.trim();
      if (trimmedKeyword.length < minSearchChars) {
        setInternalStations([]);
        setUseGooglePlaces(false);
        return;
      }

      // Extract cityId and cityName from cityContext if available
      const cityId = cityContext?.id;
      const cityName = cityContext?.name;

      setIsInternalSearchLoading(true);
      setUseGooglePlaces(false);
      setInternalSearchCompleted(false);
      setGooglePlacesSearchCompleted(false);

      try {
        const response = await searchStations(trimmedKeyword, stationPlaceType, cityId, cityName);

        // If successful and has data, use internal results
        if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
          setInternalStations(response.data);
          setUseGooglePlaces(false);
          setInternalSearchCompleted(true);
          setGooglePlacesSearchCompleted(false);
        } else {
          // If successful but data is empty array or no data, use Google Places after delay
          setInternalStations([]);
          setUseGooglePlaces(true);
          // Keep loading state during delay - don't mark as completed yet
          setInternalSearchCompleted(false);
          setGooglePlacesSearchCompleted(false);

          prepareGooglePlacesFallback();
          const googlePlaceTypes = getGooglePlaceTypesForStation(stationPlaceType);
          scheduleGooglePlacesCall(trimmedKeyword, googlePlaceTypes);
        }
      } catch (error) {
        prepareGooglePlacesFallback();
        const googlePlaceTypes = getGooglePlaceTypesForStation(stationPlaceType);
        scheduleGooglePlacesCall(trimmedKeyword, googlePlaceTypes);
      } finally {
        // Only stop internal loading if we have results or if we're not going to Google Places
        if (internalStations.length > 0 || !useGooglePlaces) {
          setIsInternalSearchLoading(false);
        }
      }
    },
    [
      enableStationSearch,
      stationPlaceType,
      cityContext,
      minSearchChars,
      clearGooglePlacesTimeout,
      prepareGooglePlacesFallback,
      getGooglePlaceTypesForStation,
      scheduleGooglePlacesCall,
    ],
  );

  // Internal search function - called after debounce and minimum character check
  const performInternalSearch = useCallback(
    async (keyword: string) => {
      clearGooglePlacesTimeout();

      if (!enableInternalSearch || !keyword || keyword.trim().length === 0) {
        setInternalPlaces([]);
        setUseGooglePlaces(false);
        return;
      }

      const trimmedKeyword = keyword.trim();
      if (trimmedKeyword.length < minSearchChars) {
        setInternalPlaces([]);
        setUseGooglePlaces(false);
        return;
      }

      setIsInternalSearchLoading(true);
      setUseGooglePlaces(false);
      setInternalSearchCompleted(false);
      setGooglePlacesSearchCompleted(false);

      try {
        const response = await searchPlaces(trimmedKeyword);

        if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
          setInternalPlaces(response.data);
          setUseGooglePlaces(false);
          setInternalSearchCompleted(true);
          setGooglePlacesSearchCompleted(false);
        } else {
          prepareGooglePlacesFallback();
          scheduleGooglePlacesCall(trimmedKeyword);
        }
      } catch (error) {
        prepareGooglePlacesFallback();
        scheduleGooglePlacesCall(trimmedKeyword);
      } finally {
        if (internalPlaces.length > 0 || !useGooglePlaces) {
          setIsInternalSearchLoading(false);
        }
      }
    },
    [
      enableInternalSearch,
      minSearchChars,
      clearGooglePlacesTimeout,
      prepareGooglePlacesFallback,
      scheduleGooglePlacesCall,
    ],
  );

  // Update refs when functions change
  useEffect(() => {
    performInternalSearchRef.current = performInternalSearch;
  }, [performInternalSearch]);

  useEffect(() => {
    performStationSearchRef.current = performStationSearch;
  }, [performStationSearch]);

  // Debounced Google Places search (when internal search is disabled)
  // Use useMemo with stable refs to prevent recreation
  const debouncedGooglePlacesSearch = useMemo(
    () =>
      debounce((searchQuery: string) => {
        if (getPlacePredictionsRef.current) {
          getPlacePredictionsRef.current({ input: searchQuery });
        }
      }, debounceDelayMs),
    [debounceDelayMs], // Only depend on debounceDelayMs, not getPlacePredictions
  );

  // Debounced internal search - waits for user to finish typing
  // Use useMemo with stable refs to prevent recreation
  const debouncedInternalSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        clearGooglePlacesTimeout();
        if (performInternalSearchRef.current) {
          performInternalSearchRef.current(keyword);
        }
      }, debounceDelayMs),
    [debounceDelayMs, clearGooglePlacesTimeout],
  );

  // Debounced station search - waits for user to finish typing
  // Use useMemo with stable refs to prevent recreation
  const debouncedStationSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        clearGooglePlacesTimeout();
        if (performStationSearchRef.current) {
          performStationSearchRef.current(keyword);
        }
      }, debounceDelayMs),
    [debounceDelayMs, clearGooglePlacesTimeout],
  );

  // Helper: Cancel all pending debounced searches (must be after debounced functions are declared)
  const cancelAllDebouncedSearches = useCallback(() => {
    debouncedInternalSearch.cancel();
    debouncedStationSearch.cancel();
    debouncedGooglePlacesSearch.cancel();
  }, [debouncedInternalSearch, debouncedStationSearch, debouncedGooglePlacesSearch]);

  // Helper: Reset search state (must be after cancelAllDebouncedSearches is declared)
  const resetSearchState = useCallback(() => {
    setShowDropdown(false);
    setInternalPlaces([]);
    setInternalStations([]);
    setUseGooglePlaces(false);
    setInternalSearchCompleted(false);
    setGooglePlacesSearchCompleted(false);
    clearGooglePlacesTimeout();
    cancelAllDebouncedSearches();
  }, [clearGooglePlacesTimeout, cancelAllDebouncedSearches]);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
      // Reset lastSelectedValue when value is externally cleared
      if (value === '') {
        setLastSelectedValue('');
        setIsPlaceSelected(false);
      }
    }
  }, [value]);

  // Cancel pending searches when cityContext changes to ensure new searches use updated context
  useEffect(() => {
    clearGooglePlacesTimeout();
    cancelAllDebouncedSearches();
  }, [cityContext, clearGooglePlacesTimeout, cancelAllDebouncedSearches]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearGooglePlacesTimeout();
      cancelAllDebouncedSearches();
    };
  }, [clearGooglePlacesTimeout, cancelAllDebouncedSearches]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setInputValue(newValue);
    onChange?.(newValue);

    // Reset place selection status when user types manually
    if (newValue !== lastSelectedValue) {
      setIsPlaceSelected(false);
      // Clear any existing validation errors
      if (form && fieldName) {
        form.setFields([{ name: fieldName, errors: [] }]);
      }
    }

    // Don't call API if empty or below minimum characters
    if (!newValue || newValue.length === 0 || newValue.length < minSearchChars) {
      resetSearchState();
      return;
    }

    // Only proceed if value changed and meets minimum character requirement
    if (newValue && newValue !== lastSelectedValue && newValue.length >= minSearchChars) {
      setShowDropdown(true);
      if (enableStationSearch) {
        debouncedStationSearch(newValue);
      } else if (enableInternalSearch) {
        debouncedInternalSearch(newValue);
      } else {
        const searchQuery = buildSearchQueryWithCity(newValue);
        debouncedGooglePlacesSearch(searchQuery);
      }
    } else {
      setShowDropdown(false);
      setInternalPlaces([]);
      setUseGooglePlaces(false);
    }
  };

  const handlePlaceSelect = (placeId: string) => {
    if (placesService) {
      placesService.getDetails(
        {
          placeId,
          fields: ['address_components', 'formatted_address', 'geometry.location', 'place_id', 'name'],
        },
        (placeDetails: unknown) => {
          if (placeDetails && typeof placeDetails === 'object') {
            const details = placeDetails as {
              address_components?: AddressComponent[];
              formatted_address?: string;
              geometry?: Geometry;
              place_id?: string;
              name?: string;
            };

            const place: PlaceResult = {
              address_components: details.address_components || [],
              formatted_address: details.formatted_address || '',
              geometry: details.geometry as Geometry,
              place_id: details.place_id || '',
              html_attributions: [],
              name: details.name || '',
            };

            setInputValue(place.formatted_address);
            setLastSelectedValue(place.formatted_address);
            setIsPlaceSelected(true);
            onChange?.(place.formatted_address);
            onPlaceSelected(place);
            setShowDropdown(false);

            // Clear any validation errors when place is selected
            if (form && fieldName) {
              form.setFields([{ name: fieldName, errors: [] }]);
            }
          }
        },
      );
    }
  };

  // Helper: Handle selection of internal place/station
  const handleInternalSelection = useCallback(
    (item: InternalPlace | InternalStation, dataKey: 'internalPlaceData' | 'internalStationData') => {
      const place = convertAddressOutputToPlaceResult(item);
      const displayName = item.name || item.formattedAddress || '';

      setInputValue(displayName);
      setLastSelectedValue(displayName);
      setIsPlaceSelected(true);
      onChange?.(displayName);

      (place as any)[dataKey] = item;
      onPlaceSelected(place);
      setShowDropdown(false);

      if (form && fieldName) {
        form.setFields([{ name: fieldName, errors: [] }]);
      }
    },
    [form, fieldName, onChange, onPlaceSelected],
  );

  const handleInternalPlaceSelect = useCallback(
    (internalPlace: InternalPlace) => handleInternalSelection(internalPlace, 'internalPlaceData'),
    [handleInternalSelection],
  );

  const handleInternalStationSelect = useCallback(
    (internalStation: InternalStation) => handleInternalSelection(internalStation, 'internalStationData'),
    [handleInternalSelection],
  );

  const handleBlur = () => {
    // Validate if form validation is enabled
    if (form && fieldName && inputValue && !isPlaceSelected) {
      form.setFields([
        {
          name: fieldName,
          errors: [validationMessageText],
        },
      ]);
    } else {
      onBlur?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className={`google-places-input ${className || ''} ${suffix ? 'pr-8' : ''}`}
        placeholder={placeholder}
        data-testid={dataTestId}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoComplete="off"
      />
      {suffix && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">{suffix}</div>
      )}

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {enableStationSearch && !useGooglePlaces ? (
            <>
              {/* Show loading when searching station API */}
              {isInternalSearchLoading && (
                <div className="px-4 py-2 text-sm text-gray-500">{t('googlePlaces.loading')}</div>
              )}
              {/* Show station results */}
              {!isInternalSearchLoading &&
                internalStations.length > 0 &&
                internalStations.map((station, index) => (
                  <div
                    key={station.id || station.googlePlaceId || `station-${index}`}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    onClick={() => handleInternalStationSelect(station)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur from firing before click
                  >
                    {station.name || station.formattedAddress || ''}
                  </div>
                ))}
              {/* Show no records only when search is completed and no results */}
              {!isInternalSearchLoading &&
                internalSearchCompleted &&
                internalStations.length === 0 &&
                inputValue &&
                inputValue.length >= minSearchChars && (
                  <div className="px-4 py-2 text-sm text-gray-500">{t('googlePlaces.noPlaceFound')}</div>
                )}
            </>
          ) : enableInternalSearch && !useGooglePlaces ? (
            <>
              {/* Show loading when searching internal API */}
              {isInternalSearchLoading && (
                <div className="px-4 py-2 text-sm text-gray-500">{t('googlePlaces.loading')}</div>
              )}
              {/* Show internal results */}
              {!isInternalSearchLoading &&
                internalPlaces.length > 0 &&
                internalPlaces.map((place, index) => (
                  <div
                    key={place.id || place.googlePlaceId || `internal-${index}`}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    onClick={() => handleInternalPlaceSelect(place)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur from firing before click
                  >
                    {place.name || place.formattedAddress || ''}
                  </div>
                ))}
              {/* Show no records only when search is completed and no results */}
              {!isInternalSearchLoading &&
                internalSearchCompleted &&
                internalPlaces.length === 0 &&
                inputValue &&
                inputValue.length >= minSearchChars && (
                  <div className="px-4 py-2 text-sm text-gray-500">{t('googlePlaces.noPlaceFound')}</div>
                )}
            </>
          ) : (
            <>
              {/* Show loading when searching (either internal, during delay, or Google Places) */}
              {(isPlacePredictionsLoading ||
                isInternalSearchLoading ||
                (useGooglePlaces && !internalSearchCompleted && (enableInternalSearch || enableStationSearch))) && (
                <div className="px-4 py-2 text-sm text-gray-500">{t('googlePlaces.loading')}</div>
              )}
              {/* Show Google Places results */}
              {/* For direct Google Places (signup/profile): show when loading is done */}
              {/* For internal search flow (trip services): show when internal search completed */}
              {!isPlacePredictionsLoading &&
                !isInternalSearchLoading &&
                ((!enableInternalSearch && !enableStationSearch) || internalSearchCompleted) &&
                placePredictions.length > 0 &&
                placePredictions.map((prediction) => (
                  <div
                    key={prediction.place_id}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                    onClick={() => handlePlaceSelect(prediction.place_id)}
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur from firing before click
                  >
                    {prediction.description}
                  </div>
                ))}
              {/* Show no records only when both searches are completed and both are empty */}
              {!isPlacePredictionsLoading &&
                !isInternalSearchLoading &&
                ((!enableInternalSearch && !enableStationSearch) || (internalSearchCompleted && googlePlacesSearchCompleted)) &&
                placePredictions.length === 0 &&
                (enableStationSearch ? internalStations.length === 0 : internalPlaces.length === 0) &&
                inputValue &&
                inputValue.length >= minSearchChars && (
                  <div className="px-4 py-2 text-sm text-gray-500">{t('googlePlaces.noPlaceFound')}</div>
                )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GooglePlacesInput;
